import React from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';



/**
 * FormA2Section — Application cum Form A2 (South Indian Bank Forex Remittance Form).
 *
 * Generated from full set of reference images.
 * Printable sheets matching the SIB Form A2 layout.
 *
 * Props:
 *   retrievedData {object}  Data entry record from useDataEntry()
 */

// ── Helper: row of individual character boxes ─────────────────────────────
const BoxRow = ({ count = 20 }) => (
  <div style={{ display: 'flex', gap: '1px', flexWrap: 'nowrap' }}>
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        style={{
          minWidth: '14px',
          height: '18px',
          border: '1px solid #666',
          flexShrink: 0,
        }}
      />
    ))}
  </div>
);

// ── Helper: single square checkbox with label ─────────────────────────────
const CheckItem = ({ label }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', marginRight: '10px', fontSize: '11px', whiteSpace: 'nowrap' }}>
    <span style={{ display: 'inline-block', width: '11px', height: '11px', border: '1px solid #444', flexShrink: 0 }} />
    {label}
  </span>
);

// ── Helper: red section banner ────────────────────────────────────────────
const Banner = ({ children, small }) => (
  <div style={{
    background: '#8B1C13',
    color: 'white',
    padding: small ? '3px 8px' : '5px 8px',
    fontSize: small ? '10px' : '11px',
    fontWeight: '600',
    margin: '8px 0',
    borderRadius: '2px',
  }}>
    {children}
  </div>
);

// ── Helper: labeled field row ─────────────────────────────────────────────
const FieldRow = ({ label, count = 35, style }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', ...style }}>
    <span style={{ fontSize: '11px', minWidth: '180px', flexShrink: 0 }}>{label}</span>
    <BoxRow count={count} />
  </div>
);

// ── Shared doc styles ─────────────────────────────────────────────────────
const sheetStyle = {
  width: '100%',
  maxWidth: '794px',
  minHeight: '1123px',
  background: 'white',
  padding: '24px',
  fontSize: '11px',
  fontFamily: 'Arial, sans-serif',
  boxSizing: 'border-box',
};

const label11 = { fontSize: '11px', margin: '3px 0' };
const bold11 = { fontSize: '11px', fontWeight: '700', margin: '4px 0' };

// ═══════════════════════════════════════════════════════════════════════════
const FormA2Section = ({ retrievedData }) => {

  const handleGeneratePdf = async () => {
    alert('Download Start');
    const pdf = new jsPDF('p', 'mm', [210, 400]);
    const options = { scale: 2, useCORS: true };

    const captureDiv = async (id) => {
      const el = document.getElementById(id);
      if (!el) return null;
      const canvas = await html2canvas(el, options);
      return canvas.toDataURL('image/png');
    };

    const ids = [
      'a2_sheet1',
      'a2_sheet2',
      'a2_sheet3',
      'a2_sheet4',
      'a2_sheet5',
      'a2_sheet6',
      'a2_sheet7',
      'a2_sheet8',
    ];
    const images = await Promise.all(ids.map(captureDiv));

    const imgWidth = 210;
    let addedPages = 0;

    for (let i = 0; i < images.length; i++) {
      if (!images[i]) continue;
      const img = await new Promise((res) => {
        const im = new Image();
        im.src = images[i];
        im.onload = () => res(im);
      });
      const imgHeight = (img.height * imgWidth) / img.width;
      if (addedPages > 0) pdf.addPage();
      pdf.addImage(images[i], 'PNG', 0, 0, imgWidth, imgHeight);
      addedPages++;
    }
    pdf.save('Form-A2.pdf');
  };

  return (
    <>
      {/* ═══════════════ Sheet 1 ═══════════════ */}
      <div id="a2_sheet1" style={sheetStyle}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div>
            <p style={{ fontSize: '15px', fontWeight: '700', color: '#8B1C13', margin: 0 }}>Application cum Form A2</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '13px', fontWeight: '700', color: '#003087', margin: 0 }}>SOUTH</p>
            <p style={{ fontSize: '13px', fontWeight: '700', color: '#003087', margin: 0 }}>INDIAN Bank</p>
          </div>
        </div>

        {/* Name */}
        <FieldRow label="Name:" count={38} />

        {/* Address */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '4px' }}>
          <span style={{ fontSize: '11px', minWidth: '180px', flexShrink: 0 }}>Address:</span>
          <div>
            <BoxRow count={38} />
            <div style={{ marginTop: '2px' }}><BoxRow count={38} /></div>
            <div style={{ marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <BoxRow count={25} />
              <span style={label11}>Customer ID</span>
              <BoxRow count={10} />
            </div>
          </div>
        </div>

        {/* PAN + Residential Status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '6px 0' }}>
          <span style={label11}>PAN No:</span>
          <BoxRow count={12} />
          <span style={label11}>Residential Status:</span>
          <CheckItem label="Resident" />
          <CheckItem label="NRI" />
          <CheckItem label="PIO/OCI" />
          <CheckItem label="Foreign National" />
        </div>

        {/* Banner 1 */}
        <Banner>Details of forex required</Banner>

        {/* Nature of Instrument */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', margin: '6px 0' }}>
          <span style={{ ...label11, minWidth: '140px' }}>Nature of Instrument</span>
          <CheckItem label="Foreign Currency DD" />
          <CheckItem label="TT" />
          <CheckItem label="FCY Notes" />
          <CheckItem label="Travel card" />
        </div>

        {/* Select Currency */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', margin: '6px 0' }}>
          <span style={{ ...label11, minWidth: '140px' }}>Select Currency</span>
          <CheckItem label="USD" />
          <CheckItem label="EUR" />
          <CheckItem label="CAD" />
          <CheckItem label="GBP" />
          <CheckItem label="AED" />
          <CheckItem label="AUD" />
          <span style={label11}>Other</span>
          <BoxRow count={4} />
        </div>

        {/* Amount in FCY + Amount in words */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', margin: '6px 0' }}>
          <div>
            <p style={{ ...label11, margin: '0 0 2px' }}>Amount in FCY (Foreign Currency)</p>
            <BoxRow count={18} />
          </div>
          <div style={{ border: '1px solid #888', padding: '4px 8px', minWidth: '180px', minHeight: '40px' }}>
            <p style={{ ...label11, margin: 0 }}>Amount in<br />words</p>
          </div>
        </div>

        {/* Amount in INR */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '6px 0' }}>
          <div>
            <p style={{ ...label11, margin: '0 0 2px' }}>Amount in INR (If foreign currency<br />not specified.)</p>
            <BoxRow count={18} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
              <span style={{ display: 'inline-block', width: '11px', height: '11px', border: '1px solid #444', flexShrink: 0, marginTop: '1px' }} />
              <span style={label11}>Inclusive of Forex Tax &amp;<br />Charges (TCS extra)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
              <span style={{ display: 'inline-block', width: '11px', height: '11px', border: '1px solid #444', flexShrink: 0, marginTop: '1px' }} />
              <span style={label11}>Exclusive of Forex Tax &amp;<br />Charges</span>
            </div>
          </div>
        </div>

        {/* Sources of fund */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '6px 0' }}>
          <span style={{ ...label11, marginRight: '4px' }}>✦ Sources of fund</span>
          <CheckItem label="Salary" />
          <CheckItem label="Business Income" />
          <CheckItem label="Rent Income" />
          <CheckItem label="Others" />
          <div style={{ borderBottom: '1px solid #888', width: '80px' }} />
        </div>

        {/* Debit Authority */}
        <div style={{ margin: '8px 0' }}>
          <p style={label11}>
            Debit Authority: I /We authorise you to debit my/our saving bank /current/EEFC/RFC account number
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', margin: '4px 0' }}>
            <BoxRow count={20} />
          </div>
          <p style={label11}>
            to effect foreign exchange remittance /Issue a draft/FCY notes/Travellers Cheque
            as detailed above (Strike out whichever is not applicable) along with your applicable charges - Commission, Foreign Conversion Tax, Service
            Tax, TCS and correspondent Bank charges to my/our account number
          </p>
          <BoxRow count={20} />
        </div>

        {/* LRS Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', margin: '8px 0', fontSize: '11px' }}>
          <tbody>
            <tr>
              <td style={{ border: '1px solid #888', padding: '4px', width: '25%' }}>
                <div>Whether under LRS<br />(Yes/No)</div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                  <CheckItem label="Yes" /><CheckItem label="No" />
                </div>
              </td>
              <td style={{ border: '1px solid #888', padding: '4px', width: '20%' }}>
                Purpose<br />Description
              </td>
              <td style={{ border: '1px solid #888', padding: '4px', width: '15%' }}>
                Purpose Code
              </td>
              <td style={{ border: '1px solid #888', padding: '4px', width: '25%' }}>
                <div>Whether the amount is taxable or not Yes/No</div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                  <CheckItem label="Yes" /><CheckItem label="No" />
                </div>
              </td>
              <td style={{ border: '1px solid #888', padding: '4px', width: '15%' }}>
                <div>Whether 15 CA CB attached or not</div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                  <CheckItem label="Yes" /><CheckItem label="No" />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>



      {/* ═══════════════ Sheet 2 ═══════════════ */}
      <div id="a2_sheet2" style={sheetStyle}>

        <Banner small>
          Additional Details (If Purpose code is family maintenance, Payment for import services, MTT related services, Education, Travel related, Etc
        </Banner>

        {/* Family Maintenance */}
        <p style={bold11}>Family Maintenance – The facility is provided towards maintenance of close relatives. Select the relationship from list below:</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', margin: '4px 0' }}>
          <CheckItem label="Spouse" />
          <CheckItem label="Father" />
          <CheckItem label="Mother" />
          <CheckItem label="Son" />
          <CheckItem label="Son's Wife" />
          <CheckItem label="Daughter" />
          <CheckItem label="Daughter's Husband" />
          <CheckItem label="Brother" />
          <CheckItem label="Sister" />
        </div>

        {/* Travel */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', margin: '6px 0', alignItems: 'center' }}>
          <span style={{ ...bold11, marginRight: '4px' }}>Travel -</span>
          <CheckItem label="Holiday trip" />
          <CheckItem label="Pilgrimage" />
          <CheckItem label="Medical" />
          <CheckItem label="Immigration/Employment" />
          <CheckItem label="Business Travel" />
          <CheckItem label="Education" />
        </div>

        {/* Traveller's Name + Passport */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '6px 0' }}>
          <span style={label11}>Traveller's Name:</span>
          <BoxRow count={16} />
          <span style={label11}>Traveller's Passport No.</span>
          <BoxRow count={10} />
        </div>

        {/* Additional Details */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '6px 0' }}>
          <span style={label11}>Additional Details:</span>
          <BoxRow count={26} />
        </div>

        {/* Payment for import of services */}
        <div style={{ margin: '8px 0' }}>
          <p style={bold11}>
            Payment for import of services (Purpose Group Nos. 02, 03, 05, 06, 07, 08, 09, 10, 11, 15, 16, or 17), please indicate:
          </p>
          <p style={label11}>*Name of the country providing ultimate services:</p>
          <p style={label11}>
            (Remitter should put a tick (✓) against an appropriate purpose code. In case of doubt/ difficulty, the AD bank should be consulted)
          </p>
        </div>

        {/* MTT */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '6px 0' }}>
          <span style={bold11}>MTT transaction-</span>
          <span style={label11}>MTT annexure attached</span>
          <CheckItem label="Yes" />
          <CheckItem label="No" />
        </div>

        {/* Beneficiary Details Banner */}
        <Banner>Beneficiary Details</Banner>

        {/* Beneficiary Name */}
        <FieldRow label="Beneficiary's Name" count={35} />

        {/* Beneficiary Address */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '4px' }}>
          <span style={{ fontSize: '11px', minWidth: '180px', flexShrink: 0 }}>Beneficiary Address:</span>
          <div>
            <BoxRow count={35} />
            <div style={{ marginTop: '2px' }}><BoxRow count={35} /></div>
            <div style={{ marginTop: '2px' }}><BoxRow count={35} /></div>
          </div>
        </div>

        {/* Name / Signature / Date row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '40px', borderTop: '1px solid #ccc', paddingTop: '8px' }}>
          <span style={label11}>Name:</span>
          <span style={label11}>Signature of Applicant:</span>
          <span style={label11}>Date:</span>
        </div>
      </div>



      {/* ═══════════════ Sheet 3 ═══════════════ */}
      <div id="a2_sheet3" style={sheetStyle}>

        <FieldRow label="Beneficiary Country:" count={35} />
        <FieldRow label="Beneficiary Bank:" count={35} />
        <FieldRow label="Beneficiary Bank Address:" count={35} />
        <FieldRow label="Beneficiary Bank's Country:" count={35} />

        <div style={{ marginBottom: '4px' }}>
          <FieldRow label="Beneficiary Bank Account/IBAN" count={35} />
          <p style={{ fontSize: '10px', color: '#555', marginLeft: '186px', marginTop: '1px' }}>
            (IBAN mandatory for remittance to UK, Europa, UAE, Saudi etc.)
          </p>
        </div>

        {/* SWIFT + Sort code */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <span style={{ ...label11, minWidth: '180px', flexShrink: 0 }}>Beneficiary Bank SWIFT Code:</span>
          <BoxRow count={14} />
          <span style={label11}>Sort code (for remittance to UK)</span>
          <BoxRow count={8} />
        </div>

        {/* Transit + BSB */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <span style={{ ...label11, minWidth: '180px', flexShrink: 0 }}>Transit Number (For remittance to Canada):</span>
          <BoxRow count={10} />
          <span style={label11}>BSB Code (For remittance to Australia)</span>
          <BoxRow count={8} />
        </div>

        <FieldRow label="Routing Number (For remittance to USA):" count={20} />

        {/* Correspondent */}
        <div style={{ marginBottom: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
            <span style={{ ...label11, minWidth: '180px', flexShrink: 0 }}>Correspondent/Intermediary Bank Name and address</span>
            <BoxRow count={22} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <BoxRow count={35} />
          </div>
        </div>

        <FieldRow label="Correspondent/Intermediary Bank Swift code" count={20} />

        {/* Name / Signature / Date */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '40px', borderTop: '1px solid #ccc', paddingTop: '8px' }}>
          <span style={label11}>Name:</span>
          <span style={label11}>Signature of Applicant:</span>
          <span style={label11}>Date:</span>
        </div>
      </div>



      {/* ═══════════════ Sheet 4 ═══════════════ */}
      <div id="a2_sheet4" style={sheetStyle}>

        {/* Correspondent Bank Charges */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '8px 0' }}>
          <span style={label11}>Correspondent Bank Charges to be borne by Remitter –</span>
          <CheckItem label="OUR" />
          <CheckItem label="Shared -SHA" />
        </div>

        {/* LRS Transaction Table */}
        <div style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '8px', margin: '8px 0', background: '#fef2f2' }}>
          <p style={{ ...label11, margin: '0 0 6px' }}>
            Details of the remittances made/transactions effected under the Liberalised Remittance Scheme in the
            current financial year (April- March)..............................
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr>
                <th style={{ background: '#8B1C13', color: 'white', padding: '4px', border: '1px solid #8B1C13', width: '8%' }}>Sl. No</th>
                <th style={{ background: '#8B1C13', color: 'white', padding: '4px', border: '1px solid #8B1C13', width: '25%' }}>Date</th>
                <th style={{ background: '#8B1C13', color: 'white', padding: '4px', border: '1px solid #8B1C13', width: '25%' }}>Amount Eq USD</th>
                <th style={{ background: '#8B1C13', color: 'white', padding: '4px', border: '1px solid #8B1C13', width: '42%' }}>Name and address of AD branch/FFMC through which the transaction has been effected</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3].map((row) => (
                <tr key={row}>
                  <td style={{ border: '1px solid #ccc', padding: '4px' }}><BoxRow count={2} /></td>
                  <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                    <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                      {['D','D','M','M','Y','Y','Y','Y'].map((ph, i) => (
                        <div key={i} style={{ width: '16px', height: '18px', border: '1px solid #888', fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>{ph}</div>
                      ))}
                    </div>
                  </td>
                  <td style={{ border: '1px solid #ccc', padding: '4px' }}><BoxRow count={10} /></td>
                  <td style={{ border: '1px solid #ccc', padding: '4px' }}><BoxRow count={16} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Declaration */}
        <p style={bold11}>✦ I/We hereby declare that</p>
        <p style={bold11}>Declaration under FEMA 10 (5):</p>
        <p style={label11}>
          I/We hereby declare that the transaction(s) the details of which are specifically mentioned in the Schedule hereunder does not involve and is not designed for
          the purpose of any contravention or evasion of the provisions of the aforesaid Act or of any rule, regulation, notification, direction or order made there under.
        </p>
        <p style={label11}>
          I/We also hereby agree and undertake to give such information/documents, before the Bank undertakes the transaction(s) and as may be required from time
          to time as will reasonably satisfy you about the transaction(s) in terms of the above declaration.
        </p>
        <p style={label11}>
          I/We also understand that if I/We refuse to comply with any such requirement or make unsatisfactory compliance therewith, the Bank shall refuse in writing to
          undertake the transaction and shall if it has reason to believe that any contravention/evasion is contemplated by me/us report the matter to Reserve Bank of India.
        </p>
        <p style={label11}>
          *I/We further declare that the undersigned has/have the authority to give this declaration and undertaking on behalf of the firm/company
        </p>

        <p style={bold11}>Declaration under Liberalised Remittance Scheme:</p>
        <p style={label11}>
          I/We hereby declare that the total amount of foreign exchange purchased from or remitted through, all sources in India during the financial year including this
          application is within the overall limit of the Liberalised Remittance Scheme prescribed by the Reserve Bank of India
        </p>
        <p style={label11}>
          I/We hereby certify that the source of funds for making the said remittance belongs to me and the foreign exchange will not be used for prohibited purposes.
          I am a person resident in India. I am not foreign National/NRI/PIO
        </p>
        <p style={label11}>
          I have not availed any credit facilities for remittance under LRS scheme for capital account transaction.
          I/We undertake to make capital account transaction under LRS from South Indian Bank only. Also i/we have not clubbed remittance amount with any of my
          /our family members
        </p>
        <p style={label11}>
          I/We confirm that if the remittance is being made for travel arrangements of resident individuals, as per enclosed list the remittance made is within the limit
          prescribed by Reserve Bank of India for the resident individuals under LRS and where the remittance is being made for foreign tourists, as per enclosed list,
          the amount of remittance does not exceed the amount of foreign exchange remitted to India by the foreign tourists
        </p>
      </div>



      {/* ═══════════════ Sheet 5 ═══════════════ */}
      <div id="a2_sheet5" style={sheetStyle}>

        <p style={bold11}>Ancillary Services to business (Business and management consultancy/technical services, advertising &amp; trade fair services etc)</p>
        <p style={label11}>
          I/We confirm that the_________________________(specify nature) Services have been rendered by beneficiary abroad with respect to invoice number/s
          _________________________(specify invoice nos.) and there is no element of advance in it. We confirm that we have not made remittances, with
          respect to consultancy for the_________________________(specify name of the project) exceeding USD 1 million.
        </p>

        <p style={bold11}>Subscription to newspapers/periodicals</p>
        <p style={label11}>
          I/We confirm that the current remittance for subscription vide invoice number/s_________________________(specify invoice nos.) does not involve any
          banned or proscribed books/magazines.
        </p>

        <p style={bold11}>Commission on exports</p>
        <p style={label11}>
          We confirm that the export commission being paid out is limited to 12.50% of the FOB value and has been/not* *declared in Shipping Bill No._______________dtd _______
          lodged as Bill No._______________with SIB and the proceeds have been realised.
        </p>
        <p style={label11}>
          **In case the amount of commission is not declared in Shipping Bill, please attach valid explanation for the omission and attach agreement with the
          buyer/agent abroad providing for payment of commission.
        </p>

        <p style={bold11}>Refunds of export proceeds on account of claims/re-import etc.</p>
        <p style={label11}>
          We confirm that the proportionate incentive against export invoice No.________________________and Shipping Bill No.__________________have been/will be
          surrendered.
        </p>

        <p style={bold11}>OFAC Declaration: In this connection, we declare, confirm and undertake that:</p>
        <p style={label11}>
          i) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;I/We hereby declare that the payment initiated complies with guidelines of US Treasury Office of Foreign Assets Control and does not directly or
          indirectly involve a sanctions target.
        </p>
        <p style={label11}>
          ii) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;I/We declare that the transaction does not have linkage with specially designated nations and blocked person (SDN)/ countries listed under OFAC/UN
          sanctions in any manner. I/we undertake not to hold the bank responsible for any of its actions or inactions in respect of the OFAC linked/UN sanctioned
          transactions.
        </p>
        <p style={label11}>
          iii) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;We also understand that as per OFAC guidelines, any transaction in which a sanctions target has an interest, and that come within the person's
          possession or control, even if the person is an intermediary and the underlying transaction does not otherwise involve a U.S. person/institution, can come under
          the OFAC scanner resulting in stringent actions against persons found to be facilitating such transactions.
        </p>
        <p style={label11}>
          iv) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;We further declare that we shall furnish any material information relating to this transaction as required by any authority/government/agency, now or in
          future. Also we authorize South Indian Bank Ltd to make available any of the material information pertains our transaction to any authority/agency/entity without
          referring the matter to us.
        </p>
        <p style={label11}>
          v) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;We will not be holding the Bank liable for any losses which we may have to bear on account of the Bank refusing to handle the requested transaction
          on account of their internal guidelines on OFAC.
        </p>
        <p style={label11}>
          vi) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;We also undertake to indemnify and/or re-imburse the Bank on any reputational and/or financial losses that the Bank may face on account of any
          misrepresentation/ non-disclosure made by us vide this request for remittance. We also understand the Bank has the right to initiate reporting to RBI/External
          Agencies and/or initiate legal action as a remedy to any losses which Bank may have suffered as a result of misrepresentation/non-disclosure on our side.
        </p>
        <p style={bold11}>
          **I/We further declare that the undersigned has the authority to give this application, declaration and undertaking on behalf of the firm/company.
        </p>

        <p style={{ ...bold11, marginTop: '12px' }}>General Declarations</p>
      </div>



      {/* ═══════════════ Sheet 6 ═══════════════ */}
      <div id="a2_sheet6" style={sheetStyle}>

        <p style={bold11}>General Declarations</p>
        <ol style={{ paddingLeft: '18px', margin: '4px 0' }}>
          <li style={label11}>
            The purpose and transaction details as mentioned above are true to the best of my knowledge and i/we shall be responsible for any incorrect detail
            provided by me/us
          </li>
          <li style={label11}>
            The remittance is not being requested for any purpose of specifically prohibited under schedule 1 or any item restricted under schedule 2 of FEMA
            (Current account transaction) Rules 2000.
          </li>
          <li style={label11}>
            The remittance is not being sent directly or indirectly to those individuals and entities identified as passing significant risk of committing acts of terrorism
            as advised separately by RBI to the banks
          </li>
          <li style={label11}>
            I/We confirm that the beneficiary is a non-resident where the purpose of remittance is for " Family maintenance and savings "or "towards personal gift
            and donations "
          </li>
          <li style={label11}>
            I/We agree that the submission of request does not necessarily imply processing of the transaction. Bank has full rights to reject the transaction based
            on regularity and internal guidance. I/We agree that the event the transaction could not be executed /debited to my /our account, after submitting the
            request for processing to the bank, on account of insufficient/ unclear balance at the time of execution of the transaction in my/our account, Bank shall
            not be responsible for not being able to process the transaction, any exchange losses incurred in this connection, due to reversal of the forex deal, can
            be charged to me /our account.
          </li>
          <li style={label11}>
            I/We agree that in the event of transaction is cancelled or revoked by me/us after submitting the request for processing to the bank, any exchange losses
            incurred in this connection, can be charged to my/our account. I/We agree that in the event the transaction being rejected by the beneficiary bank because
            of incorrect information submitted by me, any charges levied by beneficiary bank or exchange losses incurred in this connection can be charged to me or
            my /us account.
          </li>
          <li style={label11}>
            I/We further agree that once the funds remitted by me /us have been transmitted by SIB to the correspondent and or beneficiary banks, South Indian
            Bank shall not be responsible for any delays in the disbursement of such funds.
          </li>
          <li style={label11}>
            I/We agree that once fund remitted by me/us have been transmitted by SIB, Intermediary bank charges may be levied by correspondent bank /or
            beneficiary bank, which may vary from bank to bank
          </li>
          <li style={label11}>
            I/We further declare that undersigned has the authority to give this application, declaration undertaking on behalf of the firm/company. (Applicable when
            the application, declaration, undertaking is signed on behalf of firm/company)
          </li>
        </ol>

        {/* Signatures */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '40px' }}>
          <div>
            <p style={bold11}>*Signature of the natural guardian of the Applicant:</p>
            <p style={{ ...label11, marginTop: '20px' }}>Name:</p>
            <p style={label11}>Date:</p>
          </div>
          <div>
            <p style={bold11}>Signature of the Applicant:</p>
            <p style={{ ...label11, marginTop: '20px' }}>Name:</p>
            <p style={label11}>Date:</p>
          </div>
        </div>

        <p style={{ fontSize: '10px', fontStyle: 'italic', marginTop: '20px', color: '#555' }}>
          *Where the applicant is minor, the application should be countersigned by minor's natural guardian
        </p>
      </div>



      {/* ═══════════════ Sheet 7 ═══════════════ */}
      <div id="a2_sheet7" style={sheetStyle}>

        <p style={{ ...bold11, textAlign: 'center', fontSize: '12px' }}>Declaration for remittance from NRO A/c</p>
        <p style={label11}>I/We confirm that-</p>
        <ol style={{ paddingLeft: '18px', margin: '4px 0' }}>
          <li style={label11}>
            The amount remitted out of the balance held by NRO account(s) including the present request is not exceeding USD 1 million during the current financial
            year. We understand that the limit of USD 1 million is for remittance from NRO account by NRI/PIO from the entire banking system during a Financial Year.
          </li>
          <li style={label11}>
            The asset was acquired by me / us in accordance with the foreign exchange law in force at the time of acquisition or the provisions of Foreign Exchange
            Management (Acquisition and Transfer of immovable Property in India) Regulations, 2000. Also, I/We confirm that the amount does not involve the amount
            invested on non-repatriation basis and capital appreciation thereon.
          </li>
          <li style={label11}>
            Source of funds for the present remittance is not from the sale of agricultural land / plantation property / farmhouse property in India.
          </li>
          <li style={label11}>
            I/We confirm that in respect of assets acquired under a deed of settlement made by either of my / our parents or a relative as defined in Companies Act, 2013. The
            settlement has taken effect on the death of the settler.
          </li>
          <li style={label11}>
            In case of residential property purchased as a non-resident Indian, the repatriation of sale proceeds are not exceeding 2 such properties
          </li>
          <li style={label11}>
            I/We confirm that I/We have paid the applicable taxes in India on the income declared in application form.
          </li>
          <li style={label11}>
            I/We confirm that the amount to be repatriated does not exceed the amount paid for acquisition of the immovable property received through normal banking
            channels or out of funds held in FCNR(B) account of NRE account.
          </li>
          <li style={label11}>
            The said remittance is sought to be made out of the balances held in the account arising from my/our legitimate receivables in India and not by borrowing
            from any other person or a transfer from any other NRO account and if such is found to be the case, I/We will render myself/ourselves liable for penal
            action under FEMA.
          </li>
        </ol>

        <Banner>For Non Resident Individuals</Banner>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '8px 0' }}>
          <span style={label11}>1. Source of funds for the present remittance</span>
          <BoxRow count={22} />
        </div>
      </div>



      {/* ═══════════════ Sheet 8 ═══════════════ */}
      <div id="a2_sheet8" style={sheetStyle}>

        {/* Signatures for NRO / Non Resident */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
          <div>
            <p style={bold11}>*Signature of the natural guardian of the Applicant</p>
            <p style={{ ...label11, marginTop: '20px' }}>Name:</p>
            <p style={label11}>Date:</p>
          </div>
          <div>
            <p style={bold11}>Signature of the Applicant:</p>
            <p style={{ ...label11, marginTop: '20px' }}>Name:</p>
            <p style={label11}>Date:</p>
          </div>
        </div>

        <p style={{ fontSize: '10px', fontStyle: 'italic', marginTop: '16px', color: '#555' }}>
          *Where the applicant is minor, the application should be countersigned by minor's natural guardian
        </p>

        <hr style={{ border: '1px solid #ccc', margin: '20px 0' }} />

        {/* Certificate by Authorised Dealer */}
        <p style={{ ...bold11, textAlign: 'center', fontSize: '12px' }}>Certificate by the Authorised Dealer</p>
        <p style={{ ...label11, margin: '8px 0' }}>
          This is to certify that the remittance is not being made by/ to ineligible entities and that the remittance is in conformity with the instructions issued by the
          Reserve Bank from time to time under the Scheme.
        </p>

        <div style={{ marginTop: '20px' }}>
          <p style={label11}>
            Name and designation of the authorised official______________________________________________________
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '16px 0' }}>
            <span style={label11}>Stamp and seal_____________________________</span>
            <span style={label11}>Signature____________________________________</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '16px 0' }}>
            <span style={label11}>Place____________________________________</span>
            <span style={label11}>Date</span>
          </div>
        </div>

        <Banner>For Branch's use only</Banner>
        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '12px 0' }}>
          <span style={label11}>Work item No.___________________________</span>
          <span style={label11}>Branch ID___________________________</span>
          <span style={label11}>AD Code___________________________</span>
        </div>

        {/* Footer Signature */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '60px', borderTop: '1px solid #eee', paddingTop: '8px' }}>
          <span style={label11}>Name:</span>
          <span style={label11}>Signature of Applicant.</span>
          <span style={label11}>Date:</span>
        </div>
      </div>



      {/* ── Download Button ── */}
      <div className="d-flex justify-content-center py-2">
        <button
          type="button"
          id="forma2-download-btn"
          className="btn btn-orange text-uppercase fw-bold px-4 py-2"
          onClick={handleGeneratePdf}
        >
          📥 Download Form A2
        </button>
      </div>
    </>
  );
};

export default FormA2Section;
